import { useCallback } from 'react'
import { useRouter } from 'expo-router'
import { getVoiceAssessmentSessionDefinitionId } from '@/src/config/voice-assessment-session'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { pushNewVoiceAssessmentRoute } from '@/src/features/voice-assessment/utils/voice-assessment-route'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { sessionDefinitionQueryOptions } from '@/src/queries/session-queries'
import { appQueryOptions } from '@/src/queries/view-queries'
import { useQueryClient } from '@tanstack/react-query'

interface UseStartVoiceAssessmentSessionResult {
  /**
   * Navigate into the standalone voice-assessment intro route and warm
   * the session-definition cache in parallel. The actual backend
   * session is minted lazily inside the flow when the user explicitly
   * taps the in-flow "Start assessment" CTA, so this entry point stays
   * free of API calls.
   */
  startVoiceAssessmentSession: () => void
}

/**
 * Opens the voice-assessment flow for a fresh assessment from the
 * dashboard reminder banner and the statistics empty-state CTA. Only
 * navigation + prefetch happens here; the backend session is minted
 * later from inside the flow's intro screen so the user sees a proper
 * loading state on the actual start button instead of on the entry
 * point that just opens the screen.
 */
export function useStartVoiceAssessmentSession(): UseStartVoiceAssessmentSessionResult {
  const router = useRouter()
  const queryClient = useQueryClient()
  const contentfulLanguage = useContentfulLocale()
  const { backendUser } = useAppSession()

  const startVoiceAssessmentSession = useCallback(() => {
    void queryClient
      .ensureQueryData(appQueryOptions(contentfulLanguage))
      .then((app) =>
        queryClient.prefetchQuery(
          sessionDefinitionQueryOptions(
            getVoiceAssessmentSessionDefinitionId({
              app,
              user: backendUser,
            }),
            contentfulLanguage,
          ),
        ),
      )
      .catch(() => undefined)
    pushNewVoiceAssessmentRoute(router)
  }, [backendUser, contentfulLanguage, queryClient, router])

  return {
    startVoiceAssessmentSession,
  }
}
